import React, { useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { isIEBrowser, isEdgeBrowser } from 'utils';
import { StickyBar, Button } from 'components/common';
import { AddToCart, CartLineQuantityInput } from 'lib/cart';
import {
  NutritionalValue,
  sumNutritionalValues,
  multiplyNutritionalValue,
  AdditionalNutrition,
  Nutrient,
} from 'lib/nutrition';
import {
  isCustomizableMeal,
  isEmptyNutritionalValue,
  isMerch,
  typedef,
} from 'lib/product';

import useProductCustomization from '../useProductCustomization';
import ProductPrice from '../ProductPrice';
import { useProductVariantAddonOptions } from 'lib/productAddon';
import { isNil } from 'lib/javascript';
import cn from 'classnames';
import ProductNutrient from '../ProductNutrient';

const ProductStickyBar = ({ product, onFirstItemAdded }) => {
  const {
    selectedVariant,
    selectedAddonOptionValueIds,
    quantity,
    setQuantity,
  } = useProductCustomization();

  const {
    isLoading,
    selectAddonOptionValueById,
    selectIngredientById,
  } = useProductVariantAddonOptions(selectedVariant?.id);

  const showNutritionValue = useMemo(() => {
    const isCustomizableProduct = isCustomizableMeal(product);

    if (isCustomizableProduct) return true;

    const isMerchProduct = isMerch(product);
    const isEmptyNutrition = isEmptyNutritionalValue(
      selectedVariant?.nutrition,
    );

    return !isMerchProduct && !isEmptyNutrition;
  }, [product, selectedVariant]);

  if (isLoading) return null;

  const calculateIngredientNutritionalValue = (
    ingredient,
    portion,
  ) => {
    if (!ingredient?.nutrition) return {};

    const {
      constant,
      proportional,
      base_portion,
    } = ingredient.nutrition;

    return sumNutritionalValues(
      constant,
      multiplyNutritionalValue(proportional, portion / base_portion),
    );
  };

  const calculateAddonOptionValueNutritionalValue = (
    addonOptionValue,
  ) => {
    if (!addonOptionValue) return {};

    const {
      ingredient: ingredientId,
      consume_quantity,
    } = addonOptionValue;

    const ingredient = selectIngredientById(ingredientId);

    return calculateIngredientNutritionalValue(
      ingredient,
      consume_quantity,
    );
  };

  const calculateProductVariantNutritionalValue = (
    productVariant,
  ) => {
    return productVariant?.nutrition?.constant;
  };

  const calculateNutritionalValue = ({
    productVariant,
    addonOptionValues,
  }) => {
    const addonOptionsNutritionalValue = addonOptionValues.reduce(
      (result, addonOptionValue) => {
        result = sumNutritionalValues(
          result,
          calculateAddonOptionValueNutritionalValue(addonOptionValue),
        );

        return result;
      },
      {},
    );

    const productVariantNutritionalValue = calculateProductVariantNutritionalValue(
      productVariant,
    );

    return sumNutritionalValues(
      addonOptionsNutritionalValue,
      productVariantNutritionalValue,
    );
  };

  const selectedAddonOptionValues = selectedAddonOptionValueIds.map(
    (selectedAddonOptionValueId) =>
      selectAddonOptionValueById(selectedAddonOptionValueId),
  );

  const nutritionalValue = calculateNutritionalValue({
    productVariant: selectedVariant,
    addonOptionValues: selectedAddonOptionValues,
  });

  const displayedNutritionalValues = () => {
    const constantValues = [];
    const productVariantNutritionalValues =
      selectedVariant?.nutrition?.constant;

    Object.keys(productVariantNutritionalValues ?? []).forEach(
      (key) => {
        const value = productVariantNutritionalValues[key];
        if (!constantValues[key]) {
          constantValues[key] = [];
        }

        constantValues[key].push(value);
      },
    );

    const ingredientsProportionalValues = selectedAddonOptionValues.reduce(
      (result, addonOption) => {
        const ingredientId = addonOption?.ingredient;
        const ingredient = selectIngredientById(ingredientId);

        if (ingredient && ingredient?.nutrition) {
          const { proportional, constant } = ingredient.nutrition;

          Object.keys(proportional).forEach((key) => {
            const value = proportional[key] ?? constant[key];

            if (!result[key]) {
              result[key] = [];
            }

            result[key].push(value);
          });
        }
        return result;
      },
      {},
    );

    const values = selectedAddonOptionValues?.length
      ? ingredientsProportionalValues
      : constantValues;

    const _displayedNutritionalValues = {};

    Object.keys(values).forEach((key) => {
      const nutrition = values[key];

      if (!_displayedNutritionalValues[key]) {
        _displayedNutritionalValues[key] = [];
      }

      const shouldShow = nutrition.every((value) => !isNil(value));

      _displayedNutritionalValues[key] = shouldShow;
    });

    return _displayedNutritionalValues;
  };

  return (
    <StickyBar>
      <Container
        className={cn(
          'tw-relative tw-px-0',
          showNutritionValue ? 'tw-py-0' : 'tw-py-4 md:tw-py-6',
        )}
      >
        {showNutritionValue && (
          <AdditionalNutrition
            nutritionalValue={nutritionalValue}
            displayedNutritionalValues={displayedNutritionalValues()}
          />
        )}
        <div className="tw-flex tw-items-start md:tw-items-center tw-justify-between tw-w-full tw-flex-col md:tw-flex-row tw-relative tw-bg-white tw-z-20">
          {showNutritionValue && (
            <div className="tw-flex tw-gap-4 md:tw-gap-6 tw-flex-1 tw-w-full tw-items-end tw-justify-between md:tw-justify-start tw-px-4 lg:tw-px-8 tw-py-6">
              <Nutrient
                name="Calories"
                value={nutritionalValue?.calorie ?? 0}
              />
              <Nutrient
                name="Fats"
                value={nutritionalValue?.fat ?? 0}
                unit="g"
              />
              <Nutrient
                name="Carbs"
                value={nutritionalValue?.carb ?? 0}
                unit="g"
              />
              <Nutrient
                name="Protein"
                value={nutritionalValue?.protein ?? 0}
                unit="g"
              />
              <Nutrient
                name="Sodium"
                value={nutritionalValue?.sodium ?? 0}
                unit="mg"
              />
            </div>
          )}
          <div className="tw-flex tw-gap-4 tw-items-center tw-justify-end tw-p-4 tw-pt-0 md:tw-px-8 md:tw-py-6 tw-w-full">
            <ProductPrice
              variant={selectedVariant}
              addonOptionValues={selectedAddonOptionValues}
              className="tw-mb-0 tw-inline-flex tw-font-semibold tw-mr-0"
            />

            <div className="tw-max-w-[160px] lg:tw-max-w-[120px]">
              <CartLineQuantityInput
                id="qty"
                name="quantity"
                productVariant={selectedVariant}
                value={quantity}
                onChange={setQuantity}
                data-decimals="0"
                required
              />
            </div>

            <AddToCart
              product={product}
              productVariant={selectedVariant}
              quantity={quantity}
              addonOptionValueIds={selectedAddonOptionValueIds}
              onFirstItemAdded={onFirstItemAdded}
            >
              {({
                loading,
                isOutOfStock,
                canAddToCart,
                addToCart,
                isAddingToCart,
              }) => {
                const getLabel = () => {
                  if (loading) return 'Loading...';
                  if (isOutOfStock) return 'We Sold Out!';
                  return 'ADD TO CART';
                };

                const disabled = !canAddToCart;

                return (
                  <Button
                    style={
                      isIEBrowser() || isEdgeBrowser()
                        ? { minHeight: '4rem' }
                        : {}
                    }
                    disabled={disabled}
                    loading={isAddingToCart}
                    color={disabled ? 'dark' : 'primary'}
                    onClick={addToCart}
                    className="tw-w-full md:tw-w-auto"
                  >
                    {getLabel()}
                  </Button>
                );
              }}
            </AddToCart>
          </div>
        </div>
        {/* <Row className="tw-z-20 tw-relative tw-bg-white"> */}
        {/* <Col
            sm={showNutritionValue ? 6 : 12}
            className={cn(
              'd-flex',
              showNutritionValue
                ? 'd-none'
                : '[&>div]:tw-mr-0 tw-space-x-4 md:tw-space-x-8',
            )}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          ></Col> */}
        {/* </Row> */}
      </Container>
    </StickyBar>
  );
};

ProductStickyBar.propTypes = {
  product: typedef.Product,
  onFirstItemAdded: PropTypes.func,
};

export default ProductStickyBar;
